import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { viewportMatches } from '@system-dyg/shared/utils';
import { UiButtonComponent, UiButtonSize } from '../../atoms/button/button.component';

/** Dirección / posición de aparición del panel. Default: `right` (comportamiento histórico). */
export type UiDrawerPlacement = 'right' | 'left' | 'bottom' | 'top' | 'center' | 'bottom-sheet';

/**
 * Molécula: panel (drawer) con backdrop, título, contenido proyectado
 * y acciones de cierre/guardado. Soporta aparición lateral, vertical o centrada.
 */
@Component({
  selector: 'ui-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonComponent],
  templateUrl: './drawer.component.html',
  styleUrl: './drawer.component.scss',
  host: {
    class: 'ui-drawer-host',
  },
})
export class UiDrawerComponent {
  readonly open = model(false);
  readonly title = input('');
  /** Reemplaza el título solo en móvil cuando `mobileFullscreen` está activo. */
  readonly mobileTitle = input('');
  readonly cancelLabel = input('Cerrar');
  readonly saveLabel = input('Guardar');
  readonly actionsSize = input<UiButtonSize>('md');
  readonly blockUi = input(true);
  readonly closeOnBackdrop = input(true);
  readonly showSaveButton = input(true);
  /** Si es false, Guardar emite saved pero mantiene el drawer abierto. */
  readonly closeOnSave = input(true);
  /** Si es false, el botón secundario emite cancelled y no cierra el panel. */
  readonly closeOnCancel = input(true);
  /**
   * Ancho del panel en desktop (ej. `80vw`, `720px`).
   * Si no se define, usa el ancho por defecto (520px). En móvil sigue siendo 100vw en laterales.
   */
  readonly panelWidth = input<string | null>(null);
  /**
   * Alto del panel centrado (ej. `80dvh`). Si no se define, el alto lo marca el contenido
   * hasta el máximo del placement.
   */
  readonly panelHeight = input<string | null>(null);
  /**
   * Cuerpo en columna, sin scroll propio (el contenido se estira).
   * Para visores a pantalla completa dentro del panel.
   */
  readonly fill = input(false);
  /**
   * En móvil el panel cubre el viewport y oculta el fondo semitransparente.
   * Escritorio y tableta conservan el placement.
   */
  readonly mobileFullscreen = input(false);
  /** Aplica también en tablet el placement y el tamaño usados en móvil. */
  readonly tabletAsMobile = input(false);
  /**
   * En móvil los botones del header ocupan la primera fila y el título la segunda.
   * Escritorio y tableta conservan título y botones en la misma fila.
   */
  readonly mobileStackedHeader = input(false);
  /**
   * Posición y animación de aparición en escritorio y tableta.
   * En móvil el panel usa `bottom-sheet`, salvo `mobileFullscreen`.
   * - `right` / `left`: panel lateral a pantalla completa.
   * - `bottom` / `top` / `center`: panel centrado H+V (tipo modal).
   * - `bottom-sheet`: panel anclado al borde inferior, entra de abajo hacia arriba.
   */
  readonly placement = input<UiDrawerPlacement>('right');

  readonly closed = output<void>();
  readonly saved = output<void>();
  readonly cancelled = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly isMobileViewport = viewportMatches('(max-width: 767.98px)');
  private readonly isTabletViewport = viewportMatches('(max-width: 1024px)');
  private readonly usesMobileLayout = computed(
    () => this.isMobileViewport() || (this.tabletAsMobile() && this.isTabletViewport()),
  );

  protected readonly isVisible = computed(() => this.open());
  protected readonly effectiveActionsSize = computed(() =>
    this.usesMobileLayout() ? 'sm' : this.actionsSize(),
  );
  protected readonly effectivePlacement = computed<UiDrawerPlacement>(() =>
    this.usesMobileLayout() && !this.mobileFullscreen()
      ? 'bottom-sheet'
      : this.placement(),
  );
  protected readonly panelClass = computed(() => {
    const classes = [`ui-drawer__panel--${this.effectivePlacement()}`];
    if (this.fill()) {
      classes.push('ui-drawer__panel--fill');
    }
    if (this.mobileFullscreen()) {
      classes.push('ui-drawer__panel--mobile-fullscreen');
    }
    if (this.mobileStackedHeader()) {
      classes.push('ui-drawer__panel--stacked-header');
    }
    if (
      this.usesMobileLayout() &&
      !this.mobileFullscreen() &&
      this.placement() !== 'bottom-sheet'
    ) {
      classes.push('ui-drawer__panel--mobile-edge');
    }
    return classes.join(' ');
  });

  constructor() {
    effect(() => {
      if (!this.blockUi()) return;
      document.body.style.overflow = this.isVisible() ? 'hidden' : '';
    });

    this.destroyRef.onDestroy(() => {
      document.body.style.overflow = '';
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEsc(event: Event): void {
    if (!this.isVisible()) return;
    event.preventDefault();
    this.close();
  }

  protected close(): void {
    if (!this.isVisible()) return;
    this.open.set(false);
    this.closed.emit();
  }

  protected onCancel(): void {
    if (!this.closeOnCancel()) {
      this.cancelled.emit();
      return;
    }
    this.close();
  }

  protected save(): void {
    this.saved.emit();
    if (this.closeOnSave()) {
      this.open.set(false);
    }
  }
}
